'use server';

import { SessionService } from '@/contexts/authentication';
import { VoiceCatalogService, AIProviderConfigService } from '@/contexts/ai/server';
import type { Language } from '@/contexts/stories/types/enums';

/**
 * The preset voices the editor offers for a language (one per gender),
 * resolved through the single narration rule.
 */
export async function getVoicePresets(language: Language) {
  // The two presets the editor offers ("female" / "male") — each shows the
  // concrete voice the ONE narration rule resolves to (admin override else the
  // worker default for the narration engine). An engine without a preset for
  // the pair simply has no preset entry (custom voices only).
  const presets = await Promise.all(
    (['female', 'male'] as const).map(async (gender) => {
      try {
        const v = await AIProviderConfigService.resolvePresetVoice(language, gender);
        return {
          voiceId: v.voiceId,
          name: v.voiceName,
          previewUrl: previewFor(v.provider, v.voiceId, language),
          gender,
          category: 'premade' as const,
          isCustom: false,
        };
      } catch {
        return null;
      }
    }),
  );
  return presets.filter((p): p is NonNullable<typeof p> => p !== null);
}

/** Pre-rendered samples exist for Gemini voices only (scripts/gemini-voice-previews). */
function previewFor(provider: string, voiceId: string, language: string): string | null {
  return provider === 'google_ai_studio' ? VoiceCatalogService.geminiPreviewKey(voiceId, language) : null;
}

export async function getCustomVoices() {
  const familyId = await SessionService.getFamilyId();
  if (!familyId) return [];

  return VoiceCatalogService.getCustomVoices(familyId);
}

/** The voice LIBRARY view: every clone incl. legacy ElevenLabs ones (badged, not offered elsewhere). */
export async function getLibraryVoices() {
  const familyId = await SessionService.getFamilyId();
  if (!familyId) return [];

  return VoiceCatalogService.getCustomVoices(familyId, { includeLegacy: true });
}
